import React, { FC, useState, useEffect } from "react";
import { 
  Button, 
  Card, 
  Space, 
  Typography, 
  notification, 
  Spin, 
  Modal,
  Alert,
  Divider,
  Row,
  Col,
  Badge
} from "antd";
import { 
  EditOutlined, 
  SaveOutlined, 
  CheckCircleOutlined, 
  ExclamationCircleOutlined,
  EyeOutlined,
  DeploymentUnitOutlined,
  CopyOutlined
} from "@ant-design/icons";
import { useTernaryDarkMode } from "usehooks-ts";
import { getAuthToken } from "@/utils/auth";
import axios from "axios";
import MonacoEditor from "@monaco-editor/react";
import "./ConfigEditor.css";

const { Title, Text } = Typography;

interface ConfigEditorProps {
  configId: string;
  config: any;
  onConfigUpdate?: (newConfig: any) => void;
}

interface ValidationResult {
  isValid: boolean;
  section: string;
  errors: string[];
  warnings: string[];
}

const ConfigEditor: FC<ConfigEditorProps> = ({ configId, config, onConfigUpdate }) => {
  const { isDarkMode } = useTernaryDarkMode({ localStorageKey: "theme" });
  const [isEditing, setIsEditing] = useState(false);
  const [editedConfig, setEditedConfig] = useState<string>("");
  const [originalConfig, setOriginalConfig] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [validationResults, setValidationResults] = useState<ValidationResult[]>([]);
  const [isValidated, setIsValidated] = useState(false);
  const [showDeployModal, setShowDeployModal] = useState(false);

  useEffect(() => {
    if (config) {
      setOriginalConfig(config);
      // Clean up the config for better JSON display - convert ObjectId format
      const cleanConfig = JSON.parse(JSON.stringify(config, (key, value) => {
        // Convert MongoDB ObjectId format to string
        if (value && typeof value === 'object' && value.$oid) {
          return value.$oid;
        }
        // Convert MongoDB Date format to ISO string
        if (value && typeof value === 'object' && value.$date) {
          return value.$date;
        }
        return value;
      }));
      setEditedConfig(JSON.stringify(cleanConfig, null, 2));
    }
  }, [config]);

  const handleEdit = () => {
    setIsEditing(true);
    setIsValidated(false);
    setValidationResults([]);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setIsValidated(false);
    setValidationResults([]);
    // Use the same cleaning logic as in useEffect
    const cleanConfig = JSON.parse(JSON.stringify(originalConfig, (key, value) => {
      if (value && typeof value === 'object' && value.$oid) {
        return value.$oid;
      }
      if (value && typeof value === 'object' && value.$date) {
        return value.$date;
      }
      return value;
    }));
    setEditedConfig(JSON.stringify(cleanConfig, null, 2));
  };

  const validateJsonStructure = (jsonString: string): { isValid: boolean; error?: string; parsed?: any } => {
    try {
      const parsed = JSON.parse(jsonString);
      return { isValid: true, parsed };
    } catch (error) {
      return { 
        isValid: false, 
        error: error instanceof Error ? error.message : "Invalid JSON format" 
      };
    }
  };

  const validateConfigSection = async (section: string, sectionData: any): Promise<ValidationResult> => {
    const result: ValidationResult = {
      isValid: true,
      section,
      errors: [],
      warnings: []
    };

    try {
      // Basic structure validation
      if (!sectionData) {
        result.errors.push(`${section} section is missing`);
        result.isValid = false;
        return result;
      }

      switch (section.toLowerCase()) {
        case 'source':
          if (!sectionData.url && !sectionData.azureConfig && !sectionData.databaseConfig) {
            result.errors.push("Source must have a URL, Azure config, or database config");
            result.isValid = false;
          }
          if (!sectionData.requestMethod && !sectionData.azureConfig && !sectionData.databaseConfig) {
            result.errors.push("Source must have a request method");
            result.isValid = false;
          }
          if (sectionData.url && !isValidUrl(sectionData.url)) {
            result.warnings.push("Source URL format may be invalid");
          }
          break;

        case 'destination':
          if (!sectionData.url && !sectionData.azureConfig && !sectionData.databaseConfig) {
            result.errors.push("Destination must have a URL, Azure config, or database config");
            result.isValid = false;
          }
          if (!sectionData.requestMethod && !sectionData.azureConfig && !sectionData.databaseConfig) {
            result.errors.push("Destination must have a request method");
            result.isValid = false;
          }
          if (sectionData.url && !isValidUrl(sectionData.url)) {
            result.warnings.push("Destination URL format may be invalid");
          }
          break;

        case 'datamapping':
          if (Array.isArray(sectionData)) {
            sectionData.forEach((mapping: any, index: number) => {
              if (!mapping.source || !mapping.target) {
                result.errors.push(`Data mapping ${index + 1}: Missing source or target`);
                result.isValid = false;
              }
              if (!mapping.sourcePath || !mapping.targetPath) {
                result.warnings.push(`Data mapping ${index + 1}: Missing source or target path`);
              }
            });
          }
          break;

        case 'enrichment':
          if (Array.isArray(sectionData)) {
            sectionData.forEach((enrichment: any, index: number) => {
              if (!enrichment.url && !enrichment.azureConfig && !enrichment.databaseConfig) {
                result.warnings.push(`Enrichment ${index + 1}: Missing URL or config`);
              }
            });
          }
          break;
      }

      // Call backend validation API
      const token = getAuthToken();
      if (token) {
        const baseUrl = import.meta.env.VITE_WEBSITE_API_BASE_URL || '';
        const response = await axios.post(`${baseUrl}/api-connectors/validate-config-section`, {
          section: section,
          data: sectionData
        }, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.data && !response.data.isValid) {
          result.errors.push(...(response.data.errors || []));
          result.warnings.push(...(response.data.warnings || []));
          result.isValid = false;
        }
      }
    } catch (error) {
      result.errors.push(`Validation error: ${error instanceof Error ? error.message : 'Unknown error'}`);
      result.isValid = false;
    }

    return result;
  };

  const isValidUrl = (url: string): boolean => {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  const handleValidate = async () => {
    setValidating(true);
    setValidationResults([]);

    try {
      // First validate JSON structure
      const jsonValidation = validateJsonStructure(editedConfig);
      if (!jsonValidation.isValid) {
        notification.error({
          message: "JSON Validation Failed",
          description: jsonValidation.error,
          placement: "topRight"
        });
        setValidating(false);
        return;
      }

      const parsedConfig = jsonValidation.parsed;
      const results: ValidationResult[] = [];

      // Validate each section
      const sections = ['source', 'destination', 'dataMapping', 'enrichment'];
      
      for (const section of sections) {
        if (parsedConfig[section]) {
          const result = await validateConfigSection(section, parsedConfig[section]);
          results.push(result);
        }
      }

      setValidationResults(results);
      
      const hasErrors = results.some(r => !r.isValid);
      setIsValidated(!hasErrors);

      if (hasErrors) {
        notification.error({
          message: "Validation Failed",
          description: "Please fix the validation errors before publishing",
          placement: "topRight"
        });
      } else {
        notification.success({
          message: "Validation Successful",
          description: "Configuration is valid and ready to publish",
          placement: "topRight"
        });
      }
    } catch (error) {
      notification.error({
        message: "Validation Error",
        description: error instanceof Error ? error.message : "Unknown validation error",
        placement: "topRight"
      });
    } finally {
      setValidating(false);
    }
  };

  const handlePublish = () => {
    setShowDeployModal(true);
  };

  const handleCopyJson = async () => {
    try {
      await navigator.clipboard.writeText(editedConfig);
      notification.success({
        message: "JSON Copied",
        description: "Configuration JSON has been copied to clipboard",
        placement: "topRight",
        duration: 2
      });
    } catch (error) {
      // Fallback for browsers that don't support clipboard API
      const textArea = document.createElement('textarea');
      textArea.value = editedConfig;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      
      notification.success({
        message: "JSON Copied",
        description: "Configuration JSON has been copied to clipboard",
        placement: "topRight",
        duration: 2
      });
    }
  };

  const confirmPublish = async () => {
    setPublishing(true);
    setShowDeployModal(false);

    try {
      const parsedConfig = JSON.parse(editedConfig);
      const token = getAuthToken();
      
      if (!token) {
        throw new Error("No authentication token found");
      }

      const baseUrl = import.meta.env.VITE_WEBSITE_API_BASE_URL || '';
      
      // Update the config
      const response = await axios.patch(`${baseUrl}/api-configs/${configId}`, parsedConfig, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.status === 200) {
        // Create deployment version (following updateConnector logic)
        try {
          await axios.post(`${baseUrl}/api-connectors/${configId}/deploy`, {
            config: parsedConfig,
            version: new Date().toISOString(),
            deployedBy: 'config-editor'
          }, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });
        } catch (deployError) {
          console.warn("Deployment versioning failed, but config was updated:", deployError);
        }

        setOriginalConfig(parsedConfig);
        setIsEditing(false);
        setIsValidated(false);
        setValidationResults([]);
        
        if (onConfigUpdate) {
          onConfigUpdate(parsedConfig);
        }

        notification.success({
          message: "Configuration Published",
          description: "Configuration has been updated and deployed successfully",
          placement: "topRight"
        });
      }
    } catch (error) {
      console.error("Error publishing config:", error);
      notification.error({
        message: "Publish Failed",
        description: error instanceof Error ? error.message : "Failed to publish configuration",
        placement: "topRight"
      });
    } finally {
      setPublishing(false);
    }
  };

  const renderValidationResults = () => {
    if (validationResults.length === 0) return null;

    return (
      <Card title="Validation Results" size="small" style={{ marginTop: 16 }}>
        <Space direction="vertical" style={{ width: '100%' }}>
          {validationResults.map((result, index) => (
            <div key={index}>
              <Row align="middle" gutter={8}>
                <Col>
                  <Badge 
                    status={result.isValid ? "success" : "error"} 
                    text={
                      <Text strong style={{ textTransform: 'capitalize' }}>
                        {result.section}
                      </Text>
                    }
                  />
                </Col>
                <Col>
                  {result.isValid ? (
                    <CheckCircleOutlined style={{ color: '#52c41a' }} />
                  ) : (
                    <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />
                  )}
                </Col>
              </Row>
              
              {result.errors.length > 0 && (
                <Alert
                  message="Errors"
                  description={
                    <ul style={{ margin: 0, paddingLeft: 20 }}>
                      {result.errors.map((error, i) => (
                        <li key={i}>{error}</li>
                      ))}
                    </ul>
                  }
                  type="error"
                  style={{ marginTop: 8 }}
                />
              )}
              
              {result.warnings.length > 0 && (
                <Alert
                  message="Warnings"
                  description={
                    <ul style={{ margin: 0, paddingLeft: 20 }}>
                      {result.warnings.map((warning, i) => (
                        <li key={i}>{warning}</li>
                      ))}
                    </ul>
                  }
                  type="warning"
                  style={{ marginTop: 8 }}
                />
              )}
              
              {index < validationResults.length - 1 && <Divider style={{ margin: '12px 0' }} />}
            </div>
          ))}
        </Space>
      </Card>
    );
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 40 }}>
        <Spin size="large" tip="Loading configuration..." />
      </div>
    );
  }

  return (
    <div className={`config-editor ${isDarkMode ? 'dark' : 'light'}`}>
      <Space direction="vertical" style={{ width: '100%' }} size="large">
        <Row justify="space-between" align="middle">
          <Col>
            <Title level={3}>Configuration Editor</Title>
            <Text type="secondary">
              View and edit your integration configuration in JSON format
            </Text>
          </Col>
          <Col>
            <Space>
              {!isEditing ? (
                <Button 
                  type="primary" 
                  icon={<EditOutlined />}
                  onClick={handleEdit}
                >
                  Edit Configuration
                </Button>
              ) : (
                <>
                  <Button onClick={handleCancel}>
                    Cancel
                  </Button>
                  <Button 
                    type="default" 
                    icon={<CheckCircleOutlined />}
                    onClick={handleValidate}
                    loading={validating}
                  >
                    {validating ? "Validating..." : "Validate"}
                  </Button>
                  {isValidated && (
                    <Button 
                      type="primary" 
                      icon={<DeploymentUnitOutlined />}
                      onClick={handlePublish}
                      loading={publishing}
                    >
                      {publishing ? "Publishing..." : "Publish"}
                    </Button>
                  )}
                </>
              )}
            </Space>
          </Col>
        </Row>

        <Card
          extra={
            <Button
              type="text"
              icon={<CopyOutlined />}
              onClick={handleCopyJson}
              title="Copy JSON to clipboard"
              className="copy-button"
              style={{ 
                border: 'none',
                boxShadow: 'none',
                padding: '4px 8px'
              }}
            />
          }
        >
          <div style={{ height: '600px', border: '1px solid #d9d9d9', borderRadius: '6px' }}>
            <MonacoEditor
              key={`config-editor-${isDarkMode ? 'dark' : 'light'}`}
              height="100%"
              language="json"
              theme={isDarkMode ? "vs-dark" : "vs-light"}
              value={editedConfig}
              onChange={(value) => setEditedConfig(value || "")}
              options={{
                readOnly: !isEditing,
                minimap: { enabled: false },
                scrollBeyondLastLine: false,
                fontSize: 14,
                lineNumbers: 'on',
                folding: true,
                wordWrap: 'on',
                formatOnPaste: true,
                formatOnType: true,
                automaticLayout: true,
                tabSize: 2,
                insertSpaces: true,
                detectIndentation: false
              }}
            />
          </div>
        </Card>

        {renderValidationResults()}

        <Modal
          title="Confirm Deployment"
          open={showDeployModal}
          onOk={confirmPublish}
          onCancel={() => setShowDeployModal(false)}
          okText="Deploy Now"
          cancelText="Cancel"
          confirmLoading={publishing}
        >
          <Space direction="vertical" style={{ width: '100%' }}>
            <Alert
              message="Important"
              description="This will update the configuration and trigger a redeployment of your integration. This action cannot be undone."
              type="warning"
              showIcon
            />
            <Text>
              Are you sure you want to publish these configuration changes?
            </Text>
          </Space>
        </Modal>
      </Space>
    </div>
  );
};

export default ConfigEditor; 